

/**
 * `index.passkeyCount` as a label, or `''` when the item holds none.
 *
 * The key is emitted by Rust ONLY when non-zero (`derive_index`), so absent is
 * the normal representation of zero and must render no badge at all — an
 * unconditional `0` would also make typing "0" in the search box match every
 * password item, since both search paths compare index values substring-wise.
 */
export function passkeyBadge(envelope, t) {
  const count = Number(envelope?.index?.passkeyCount) || 0;
  if (count <= 0) return '';
  return count === 1
    ? t('vault.passkeyBadgeOne', '1 passkey')
    : t('vault.passkeyBadgeOther', '{{count}} passkeys', { count });
}

/** One-line summary from the plaintext index (never secret values). */
export function itemSummary(envelope, t) {
  const ix = envelope.index || {};
  switch (envelope.type) {
    case 'password': {
      const base = ix.username && ix.urlHost
        ? `${ix.username} @ ${ix.urlHost}`
        : ix.username || ix.urlHost || '';
      const badge = passkeyBadge(envelope, t);
      if (!badge) return base;
      return base ? `${base} · ${badge}` : badge;
    }
    case 'sftp':
      if (!ix.host) return '';
      return `${ix.username ? `${ix.username}@` : ''}${ix.host}${ix.port ? `:${ix.port}` : ''}`;
    case 'authHeader':
      return ix.sourceEnv ? `${ix.headerName || ''} · ${ix.sourceEnv}` : ix.headerName || '';
    default:
      return '';
  }
}

export const MASK = '••••••••••••';

/**
 * Detail-pane rows: [{ id, label, value, secret, mono, multiline, badge, noCopy }].
 *
 * `mono` is for values read CHARACTER BY CHARACTER — secrets, keys, tokens —
 * where a slashed zero and an unambiguous 1/l earn the face. Hosts, ports and
 * usernames are read as words and stay in the UI font; a mono host column made
 * the card look like a config dump rather than a record.
 *
 * `badge` is for values from a CLOSED set (the auth method). A pill says "one of
 * a few" in a way plain text cannot, and it is the one thing on a credential
 * card that is a state rather than a string.
 */
export function buildFieldRows(type, data, t) {
  const d = data || {};
  const rows = [];
  const push = (id, label, value, opts = {}) => {
    if (value == null || value === '') return;
    rows.push({ id, label, value: String(value), ...opts });
  };
  switch (type) {
    case 'password':
      push('url', t('vault.fieldUrl'), d.url);
      push('username', t('vault.fieldUsername'), d.username);
      push('password', t('vault.fieldPassword'), d.password, { secret: true, mono: true });
      push('totp', t('vault.fieldTotp'), d.totp, { secret: true, mono: true });
      push('notes', t('vault.fieldNotes'), d.notes, { multiline: true });
      break;
    case 'sftp':
      push('protocol', t('vault.fieldProtocol'), (d.protocol || '').toUpperCase(), { noCopy: true });
      push('host', t('vault.fieldHost'), d.host);
      push('port', t('vault.fieldPort'), d.port);
      push('username', t('vault.fieldUsername'), d.username);
      push(
        'authMethod',
        t('vault.fieldAuthMethod'),
        d.authMethod === 'key' ? t('vault.authMethodKey') : t('vault.authMethodPassword'),
        { badge: true }
      );
      push('password', t('vault.fieldPassword'), d.password, { secret: true, mono: true });
      push('privateKey', t('vault.fieldPrivateKey'), d.privateKey, {
        secret: true,
        mono: true,
        multiline: true,
      });
      push('passphrase', t('vault.fieldPassphrase'), d.passphrase, { secret: true, mono: true });
      push('initialPath', t('vault.fieldInitialPath'), d.initialPath);
      push('notes', t('vault.fieldNotes'), d.notes, { multiline: true });
      break;
    case 'dbConnection':
      // This case was MISSING. `dbConnection` was added to the modal, to Rust's
      // ITEM_TYPES and to derive_index, but never here — so a saved DB
      // connection opened to a card with no rows at all. Same family of omission
      // as the ITEM_TYPES one: the type was added where it is WRITTEN and not
      // everywhere it is READ.
      push('engine', t('vault.fieldEngine'), d.engine, { badge: true });
      // SQLite is a file on disk: no host, no port, no database name.
      push('path', t('vault.fieldDbPath'), d.path, { mono: true });
      push('host', t('vault.fieldHost'), d.host);
      push('port', t('vault.fieldPort'), d.port);
      push('database', t('vault.fieldDatabase'), d.database);
      push('username', t('vault.fieldUsername'), d.username);
      push('password', t('vault.fieldPassword'), d.password, { secret: true, mono: true });
      push('notes', t('vault.fieldNotes'), d.notes, { multiline: true });
      break;
    case 'authHeader':
      push('headerName', t('vault.fieldHeaderName'), d.headerName);
      push('headerValue', t('vault.fieldHeaderValue'), d.headerValue, {
        secret: true,
        mono: true,
      });
      push('sourceEnv', t('vault.fieldSourceEnv'), d.sourceEnv);
      push('notes', t('vault.fieldNotes'), d.notes, { multiline: true });
      break;
    case 'note':
      push('body', t('vault.fieldBody'), d.body, { multiline: true });
      break;
    case 'custom':
      (Array.isArray(d.fields) ? d.fields : []).forEach((f, i) => {
        push(`field-${i}`, f.k || t('vault.customValue'), f.v, {
          secret: !!f.secret,
          mono: !!f.secret,
        });
      });
      push('notes', t('vault.fieldNotes'), d.notes, { multiline: true });
      break;
    default:
      break;
  }
  return rows;
}
